var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/* eslint-disable @typescript-eslint/ban-types */
import { Component, Property } from '@syncfusion/ej2-base';
import { createSvgElement, createHtmlElement, setAttributeSvg } from '../diagram/utility/dom-util';
import { Size } from '../diagram/primitives/size';
import { getFunction } from '../diagram/utility/base-util';
/**
 * Represents the Ruler component that measures the Diagram objects, indicate positions, and align Diagram elements.
 * ```html
 * <div id='ruler'>Show Ruler</div>
 * ```
 * ```typescript
 * <script>
 *   var rulerObj = new Ruler({ showRuler: true });
 *   rulerObj.appendTo('#ruler');
 * </script>
 * ```
 */
export class Ruler extends Component {
    /**
     *  Constructor for creating the Ruler Component
     *
     * @param {RulerModel} options The ruler model.
     * @param {string | HTMLElement} element The ruler element.
     */
    constructor(options, element) {
        super(options, element);
        /**   @private  */
        this.offset = 0;
        /**   @private  */
        this.scale = 1;
    }
    /**
     * Initializes the values of private members.
     *
     * @returns {void}  Initializes the values of private members.
     * @private
     */
    preRender() {
        this.unWireEvents();
        this.wireEvents();
    }
    /**
     * Renders the rulers.
     *
     * @returns {void}  Renders the rulers.
     * @private
     */
    render() {
        this.updateRulerGeometry();
        this.renderComplete();
    }
    /**
     * Core method to return the component name.
     *
     * @returns {string}  Core method to return the component name.
     * @private
     */
    getModuleName() {
        return 'Ruler';
    }
    /**
     *To destroy the ruler
     *
     * @returns {void} To destroy the ruler
     */
    destroy() {
        this.unWireEvents();
        this.notify('destroy', {});
        super.destroy();
        this.element.classList.remove('e-ruler');
    }
    /**
     * Get the properties to be maintained in the persisted state.
     *
     * @returns {string}  Get the properties to be maintained in the persisted state.
     */
    getPersistData() {
        const keyEntity = ['loaded'];
        return this.addOnPersist(keyEntity);
    }
    /**
     * Refreshes the ruler when the Ruler properties are updated\
     *
     * @returns {  void}    Refreshes the ruler when the Ruler properties are updated .\
     * @param {RulerModel} newProp - provide the newProp value.
     * @param {RulerModel} oldProp - provide the oldProp value.
     * @private
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onPropertyChanged(newProp, oldProp) {
        for (const prop of Object.keys(newProp)) {
            switch (prop) {
                case 'length':
                case 'interval':
                case 'segmentWidth':
                case 'tickAlignment':
                case 'markerColor':
                case 'thickness':
                    this.updateRuler();
                    break;
            }
        }
    }
    updateRulerGeometry() {
        this.element.style.textAlign = 'left';
        this.renderRulerSpace();
        this.updateRuler();
    }
    renderRulerSpace() {
        const rulerGeometry = this.getRulerGeometry();
        let div = document.getElementById(this.element.id + '_ruler_space');
        if (!div) {
            div = createHtmlElement('div', {
                'id': this.element.id + '_ruler_space',
                'style': 'height:' + rulerGeometry.height + 'px;width:' + rulerGeometry.width + 'px;cssFloat:' + 'left;'
            });
            this.element.appendChild(div);
        }
        return div;
    }
    updateRuler() {
        const rulerSize = this.getRulerSize();
        const rulerGeometry = this.getRulerGeometry();
        let length = 0;
        let offset = 0;
        const availableSize = new Size();
        const svg = this.getRulerSVG(rulerGeometry);
        if (svg) {
            length = this.length;
            availableSize.height = rulerSize;
            offset = this.offset;
            if (length && length !== Infinity) {
                const unitLength = length + this.segmentWidth;
                const unitOffset = offset;
                this.updateSegments(unitOffset, (unitLength + Math.abs(unitOffset)), svg, rulerSize);
            }
        }
    }
    updateSegments(start, end, svg, rulerSize) {
        let run = start;
        const trans = { trans: 0 };
        while (run < end) {
            const rulerSegment = this.getNewSegment(run, svg);
            if (rulerSegment) {
                svg.appendChild(rulerSegment.segment);
                run = this.updateSegment(start, end, rulerSegment, run, trans, rulerSize);
            }
        }
    }
    updateSegment(start, end, rulerSegment, run, trans, rulerSize) {
        const segWidth = this.updateSegmentWidth(this.scale);
        if (run === start) {
            this.startValue = Math.floor(start / segWidth) * segWidth / this.scale;
            this.startValue = (this.startValue % 1) !== 0 ? Number((this.startValue).toFixed(1)) : this.startValue;
            rulerSegment.label.textContent = this.startValue.toString();
            this.defStartValue = run = this.startValue * this.scale;
            if (this.orientation === 'Horizontal') {
                this.hRulerOffset = start - run;
            }
            else {
                this.vRulerOffset = start - run;
            }
        }
        else {
            this.startValue = (run / this.scale);
            this.startValue = (this.startValue % 1) !== 0 ? Number((this.startValue).toFixed(1)) : this.startValue;
            rulerSegment.label.textContent = this.startValue.toString();
        }
        this.updateTickLabel(rulerSegment, rulerSize);
        const translate = (this.orientation === 'Horizontal') ? ((trans.trans + 0.5) + ',0.5') : ('0.5,' + (trans.trans + 0.5));
        rulerSegment.segment.setAttribute('transform', 'translate(' + translate + ')');
        trans.trans += segWidth;
        run += segWidth;
        return run;
    }
    updateTickLabel(rulerSegment, rulerSize) {
        const bBox = rulerSegment.segment.lastChild.getBBox();
        const isHorizontal = (this.orientation === 'Horizontal') ? true : false;
        const isRightOrBottom = (this.tickAlignment === 'RightOrBottom') ? true : false;
        const x = isHorizontal ? 2 : 0;
        const y = isHorizontal ? (isRightOrBottom ? (rulerSize / 2 + (11 / 2) - (11 / 2)) :
            (rulerSize / 2 + (11 / 2))) : bBox.height;
        const translate = isRightOrBottom ? (-(bBox.width + 2) + ',' + ((rulerSize / 2) - bBox.height)) :
            (-(bBox.width + 2) + ',' + ((rulerSize / 2) - bBox.height / 2));
        const attr = isHorizontal ? { 'x': x, 'y': y } :
            { 'x': x, 'y': y, 'transform': 'rotate(270)' + 'translate(' + translate + ')' };
        setAttributeSvg(rulerSegment.segment.lastChild, attr);
    }
    getNewSegment(run, svg) {
        const segment = this.createNewTicks(run, svg);
        const label = this.createTickLabel(svg, segment);
        return { segment: segment, label: label };
    }
    createNewTicks(run, svg) {
        let tick;
        let tickInterval;
        const segmentWidth = this.updateSegmentWidth(this.scale);
        //let g: SVGElement;
        const attr = { 'class': 'e-ruler-segment' };
        const g = createSvgElement('g', attr);
        for (let i = 0; i < this.interval; i++) {
            tickInterval = segmentWidth / this.interval;
            tick = this.createTick(svg, tickInterval, i + 1, run);
            g.appendChild(tick);
        }
        return g;
    }
    getLinePoint(svg, tickInterval, length) {
        const segmentWidth = this.updateSegmentWidth(this.scale);
        const rulerSize = this.getRulerSize();
        tickInterval = tickInterval * (length - 1);
        length = ((tickInterval % segmentWidth) === 0) ? rulerSize : rulerSize * 0.3;
        return length;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    createTick(svg, tickInterval, length, run) {
        let ruler;
        //let line: SVGElement;
        let linePoint = this.getLinePoint(svg, tickInterval, length);
        const rulerSize = this.getRulerSize();
        //let args: IArrangeTickOptions;
        //let attr: Object;
        const isHorizontal = (this.orientation === 'Horizontal') ? true : false;
        const isRightOrBottom = (this.tickAlignment === 'RightOrBottom') ? true : false;
        const arrangeTick = getFunction(this.arrangeTick);
        // eslint-disable-next-line
        const args = { ruler: ruler, tickLength: linePoint, tickInterval: ((this.segmentWidth / this.interval) * (length - 1)) };
        if (arrangeTick) {
            arrangeTick(args);
        }
        linePoint = args.tickLength;
        const point = tickInterval * (length - 1);
        const x1 = isHorizontal ? point : (isRightOrBottom ? rulerSize : 0);
        const x2 = isHorizontal ? point : (isRightOrBottom ? (rulerSize - linePoint) : (rulerSize - (rulerSize - linePoint)));
        const y1 = isHorizontal ? (isRightOrBottom ? rulerSize : (rulerSize - (rulerSize - linePoint))) : point;
        const y2 = isHorizontal ? (isRightOrBottom ? (rulerSize - linePoint) : 0) : point;
        const attr = { 'x1': x1, 'y1': y1, 'x2': x2, 'y2': y2, 'stroke-width': '1', 'stroke': 'black' };
        const line = createSvgElement('line', attr);
        line.setAttribute('class', 'e-ruler-tick');
        return line;
    }
    createTickLabel(svg, segment) {
        let text;
        if (segment) {
            const attr = { 'class': 'e-ruler-tick-label' };
            text = createSvgElement('text', attr);
            segment.appendChild(text);
        }
        return text;
    }
    /**
     * @private
     * @param {number} scale
     */
    /**
     * updateSegmentWidth method\
     *
     * @returns {number}    updateSegmentWidth method .\
     * @param {string} scale - provide the scale value.
     *
     * @private
     */
    updateSegmentWidth(scale) {
        if (this.segmentWidth !== 100) {
            return this.segmentWidth;
        }
        const five = 25;
        let multiples = 1;
        let div;
        //let scaleRound: number;
        const fifty = 100;
        const scaleRound = Math.pow(2, Math.round(Math.log(scale) / Math.log(2)));
        div = fifty;
        div = (fifty / scaleRound);
        while (div > 100) {
            multiples /= 10;
            div /= 10;
        }
        while (div < 25) {
            multiples *= 10;
            div *= 10;
        }
        if (div >= five && div % five !== 0) {
            div = Math.round(div / five) * five;
        }
        return div * scale / multiples;
    }
    createMarkerLine(rulerSvg, rulerObj, attr) {
        let line;
        if (rulerObj) {
            line = rulerSvg.getElementById(rulerObj.id + '_marker');
            if (line) {
                line.parentNode.removeChild(line);
            }
            line = createSvgElement('line', attr);
        }
        return line;
    }
    /**
     * updateSegmentWidth method\
     *
     * @returns {void}    updateSegmentWidth method .\
     * @param {HTMLElement} rulerObj - Defines the ruler Object
     * @param {PointModel} currentPoint - Defines the current point for ruler Object
     * @param {number} offset - Defines the offset ruler Object
     *
     * @private
     */
    drawRulerMarker(rulerObj, currentPoint, offset) {
        let rulerSvg;
        let rulerSize;
        let scale;
        let diff;
        let i;
        let attr;
        let line;
        const isHorizontal = this.orientation === 'Horizontal' ? true : false;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rulerSvgElements = rulerObj.getElementsByTagName('svg');
        for (i = 0; i < rulerSvgElements.length; i++) {
            if (rulerSvgElements[parseInt(i.toString(), 10)]) {
                rulerSvg = rulerSvgElements[parseInt(i.toString(), 10)];
            }
            break;
        }
        if (rulerSvg) {
            rulerSize = this.getRulerSize();
            attr = {
                'id': rulerObj.id + '_marker', 'x1': 0, 'y1': 0, 'x2': (isHorizontal ? 0 : rulerSize),
                'y2': (isHorizontal ? rulerSize : 0), 'stroke': this.markerColor, 'stroke-width': 1.5,
                'class': 'e-d-ruler-marker'
            };
            line = this.createMarkerLine(rulerSvg, rulerObj, attr);
            scale = this.scale;
            diff = this.offset - this.defStartValue;
            const point = isHorizontal ? currentPoint.x : currentPoint.y;
            const move = (point * scale) + offset + diff;
            line.setAttribute('transform', 'translate(' + (isHorizontal ? ((move + 0.5) + ' 0.5') : ('0.5 ' + (move + 0.5))) + ')');
            rulerSvg.appendChild(line);
        }
    }
    getRulerGeometry() {
        return new Size(this.element ? this.element.getBoundingClientRect().width : 0, this.element ? this.element.getBoundingClientRect().height : 0);
    }
    getRulerSize() {
        return this.thickness;
    }
    getRulerSVG(rulerGeometry) {
        let rulerSpace;
        const rulerSize = this.getRulerSize();
        let svg;
        if (this.element) {
            rulerSpace = document.getElementById(this.element.id + '_ruler_space');
            if (rulerSpace) {
                const attr = {
                    'id': this.element.id + '_Ruler_svg',
                    width: this.orientation === 'Horizontal' ? (rulerGeometry.width + 200) : rulerSize + 'px',
                    height: this.orientation === 'Horizontal' ? rulerSize : (rulerGeometry.height + 200) + 'px',
                    style: 'position:inherit;'
                };
                svg = createSvgElement('svg', attr);
                if (rulerSpace.childNodes.length > 0) {
                    for (let i = rulerSpace.childNodes.length - 1; i >= 0; i--) {
                        rulerSpace.childNodes[parseInt(i.toString(), 10)].parentNode.removeChild(rulerSpace.childNodes[parseInt(i.toString(), 10)]);
                    }
                }
                rulerSpace.appendChild(svg);
            }
        }
        return svg;
    }
    /**
     * Method to bind events for the ruler \
     *
     * @returns {void}    Method to bind events for the ruler .\
     * @private
     */
    wireEvents() {
        //wire Events
    }
    /**
     *  Method to unbind events for the ruler \
     *
     * @returns {void}     Method to unbind events for the ruler .\
     * @private
     */
    unWireEvents() {
        //unWire Events
    }
}
__decorate([
    Property(5)
], Ruler.prototype, "interval", void 0);
__decorate([
    Property(100)
], Ruler.prototype, "segmentWidth", void 0);
__decorate([
    Property('Horizontal')
], Ruler.prototype, "orientation", void 0);
__decorate([
    Property('RightOrBottom')
], Ruler.prototype, "tickAlignment", void 0);
__decorate([
    Property('red')
], Ruler.prototype, "markerColor", void 0);
__decorate([
    Property(25)
], Ruler.prototype, "thickness", void 0);
__decorate([
    Property(null)
], Ruler.prototype, "arrangeTick", void 0);
__decorate([
    Property(400)
], Ruler.prototype, "length", void 0);
